import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { DISPLAY } from './brand';

// Circular progress used for "done today" and for each card's days-of-cover gauge. The arc draws in
// on mount (and re-animates when `value` changes); reduced motion jumps straight to the final state.
const ProgressRing: React.FC<{
    value: number;            // 0..1
    size?: number;
    stroke?: number;
    color?: string;
    track?: string;
    label: React.ReactNode;
    sublabel?: React.ReactNode;
    ariaLabel: string;
}> = ({ value, size = 84, stroke = 8, color = '#DC5829', track = 'rgba(26,20,15,0.08)', label, sublabel, ariaLabel }) => {
    const reduceMotion = useReducedMotion();
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const v = Math.max(0, Math.min(1, value));
    return (
        <Box role="img" aria-label={ariaLabel} sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
                <motion.circle
                    cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
                    strokeDasharray={c}
                    initial={reduceMotion ? false : { strokeDashoffset: c }}
                    animate={{ strokeDashoffset: c * (1 - v) }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                />
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: '-0.03em', fontSize: size >= 80 ? '1.3rem' : '1rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{label}</Typography>
                {sublabel && <Typography sx={{ fontSize: '0.56rem', fontWeight: 800, letterSpacing: '0.06em', color: '#8A8078', textTransform: 'uppercase', mt: 0.3 }}>{sublabel}</Typography>}
            </Box>
        </Box>
    );
};

export default ProgressRing;
